import allocateManagementApi from "@api/allocateManagementApi";
import productApi, { ProductWarehouseStockItem } from "@api/productApi";
import specialShipmentApi, {
  ProductWarehouseInfo,
  SSProductItem,
  SpecialShipmentDetail,
} from "@api/sharing/specialShipmentApi";
import PopupBackground from "@component/PopupBackground";
import IntegratedTable from "@component/Table/IntegratedTable";
import { GoodsCondition, RentrapStorageKey, storageTypeOptions } from "@constant/StorageType";
import { WarehouseCode } from "@constant/WarehouseCodeValue";
import useDebounce from "@hooks/useDebounce";
import {
  createSpecialShipmentProduct,
  deleteSpecialShipmentProduct,
  fetchSpecialShipmentProductList,
  specialShipmentState,
  updateProductParams,
  updateSpecialShipmentProduct,
} from "@redux/sharing/specialShipmentSlice";
import getAvailableExpiryTypeOptions from "@utils/getAvailableExpiryTypeOptions";

import { ExclamationCircleOutlined, PlusCircleTwoTone } from "@ant-design/icons";
import { Button, Col, Form, FormInstance, Input, InputNumber, message, Modal, Row, Select, Space, Spin } from "antd";
import { ValidateStatus } from "antd/es/form/FormItem";
import { ColumnsType } from "antd/lib/table";
import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import styled from "styled-components";
import { StyledSection, TableWrapper } from "src/styles/common";
import fontStyle from "src/styles/fontStyle";
import { SpecialShipmentPurpose } from "./specialShipmentConstants";

const PopupContent = styled.div`
  max-width: 70vw;
  padding: 20px;
  background: ${({ theme }) => theme.colorNeutral100};
  max-height: 90vh;
  overflow: auto;
  width: 600px;
  && .ant-form-item-required.ant-form-item-no-colon::before {
    display: none;
  }
  && .ant-form-item-required.ant-form-item-no-colon::after {
    display: inline-block;
    margin-right: 4px;
    color: #ff4d4f;
    font-size: 14px;
    font-family: SimSun, sans-serif;
    line-height: 1;
    content: "*";
  }
`;

const Notice = styled.span`
  margin-top: 5px;
  margin-left: 10px;
  color: ${({ theme }) => theme.colorNeutral500};
  ${fontStyle("14px", "22px")};
`;

type Props = {
  itemDetails: SpecialShipmentDetail;
};

const PREFIX_CONSUMABLES = ["BX", "AC", "PP", "PK"];
const CONSUMABLES_SKU_ERROR_MSG = "耗材 SKU 須以 BX / AC / PP / PK 開頭";
/**
 * 後端以 "MaxCanSales" 做為虛擬效期（非真實效期）
 * 此列無對應真實庫存，若讓使用者選擇會導致下單異常
 * 後端移除會破壞其內部邏輯，故協議由前端過濾，不顯示給使用者
 */
const BACKEND_VIRTUAL_EFFECTIVE_DATE = "MaxCanSales";
type StockResult = { stockInfo: ProductWarehouseStockItem[] }[];
type LookupResult = { ok: true; info: ProductWarehouseInfo[]; stock: StockResult } | { ok: false; error: string };

/**
 * 虛擬倉只有良品已配額度，沒有倉別與即期概念，轉成總倉庫存格式時倉別固定良品倉、一律非即期供Modal下拉使用
 */
async function fetchVirtualStock(sku: string, warehouseCode: string, retailerId: number): Promise<StockResult> {
  const { results } = await allocateManagementApi.fetchVirtualWarehouseList({
    sku,
    retailerId,
    limit: 100,
    offset: 0,
  });
  if (results.length === 0) return [];
  return [
    {
      stockInfo: results.map((item) => ({
        warehouseCode,
        storageType: RentrapStorageKey.GOOD,
        effectiveDate: item.effectiveDate,
        batch: item.batch,
        canSalesQty: item.canUseQty,
        isExpiring: false,
      })),
    },
  ];
}

async function lookupProductBySku(
  sku: string,
  warehouseCode: string,
  virtualStockRetailerId?: number,
): Promise<LookupResult> {
  try {
    const stockRequest =
      virtualStockRetailerId === undefined
        ? productApi.fetchProductWarehouseStockList({ sku, warehouseCode, byEffectiveDate: false })
        : fetchVirtualStock(sku, warehouseCode, virtualStockRetailerId);
    const [info, stock] = await Promise.all([
      specialShipmentApi.fetchProductWarehouseInfo(sku, warehouseCode),
      stockRequest,
    ]);
    if (info.length === 0) return { ok: false, error: "查無此商品" };
    if (stock.length === 0) return { ok: false, error: "查無商品庫存" };
    return { ok: true, info, stock };
  } catch {
    return { ok: false, error: "查詢失敗" };
  }
}

function handleSkuLookupError(
  errorMsg: string,
  setSkuError: React.Dispatch<React.SetStateAction<{ validateStatus: ValidateStatus; errorMsg: string | null }>>,
  form: FormInstance<any>,
) {
  setSkuError({ validateStatus: "error", errorMsg });
  form.setFieldsValue({ productName: undefined, weightedCost: undefined, qty: undefined, canSalesQty: undefined });
}

const buildSkuRules = (
  isConsumables: boolean,
  skuError: { validateStatus: ValidateStatus; errorMsg: string | null },
) => [
  { required: true, message: "" },
  {
    validator: (_: any, value: string) => {
      if (!isConsumables || !value) return Promise.resolve();
      return PREFIX_CONSUMABLES.some((p) => value.startsWith(p))
        ? Promise.resolve()
        : Promise.reject(new Error(CONSUMABLES_SKU_ERROR_MSG));
    },
  },
  {
    validator: () => (skuError.validateStatus === "success" ? Promise.resolve() : Promise.reject(skuError.errorMsg)),
  },
];

type StockList = {
  [key: string]: ProductWarehouseStockItem[];
};

const groupStockByKey = (list: ProductWarehouseStockItem[], key: keyof ProductWarehouseStockItem): StockList => {
  return list.reduce<StockList>((acc, item) => {
    const objKey = String(item[key] ?? "null");
    acc[objKey] ??= [];
    acc[objKey].push(item);
    return acc;
  }, {});
};

export default function SpecialShipmentProduct({ itemDetails }: Props) {
  const dispatch = useDispatch();
  const { shipmentId: shipmentIdStr } = useParams();
  const [modalForm] = Form.useForm();
  const [showModal, setShowModal] = useState(false);
  const [editProduct, setEditProduct] = useState<SSProductItem | null>(null);
  const shipmentId = Number(shipmentIdStr);

  const { isFetching, specialShipmentDetail, specialShipmentProductList, productParams } =
    useSelector(specialShipmentState);
  const { results: productResults, count } = specialShipmentProductList;
  // 黑貓倉已停用，黑貓單一律唯讀
  const canEdit =
    (specialShipmentDetail?.canEdit || false) && specialShipmentDetail?.warehouseCode !== WarehouseCode.BLACK_CAT;
  const isConsumables = itemDetails.purpose === SpecialShipmentPurpose.CONSUMABLES;
  // 訂購緣由為退倉、且品況為壞品時不帶通路 id（跟無通路的單一樣查總倉），其餘帶出貨單的通路 id
  const isWithdrawBad =
    itemDetails.purpose === SpecialShipmentPurpose.WITHDRAW && itemDetails.condition === GoodsCondition.BAD;
  const virtualStockRetailerId = isWithdrawBad ? undefined : (itemDetails.retailerId ?? undefined);

  const handleOnSubmitProduct = (type: "create" | "update", values: any, productId?: number) => {
    if (type === "create") {
      dispatch(createSpecialShipmentProduct({ shipmentId, payload: values }));
    }
    if (type === "update" && productId) {
      dispatch(updateSpecialShipmentProduct({ shipmentId, productId, payload: values }));
    }
  };

  const RenTrapColumns: ColumnsType<any> = [
    {
      title: "ID",
      dataIndex: "id",
    },
    {
      title: "SKU(品號)",
      dataIndex: "sku",
    },
    {
      title: "barcode",
      dataIndex: "barcode",
    },
    {
      title: "商品名稱",
      dataIndex: "productName",
    },
    {
      title: "成本",
      dataIndex: "cost",
    },
    {
      title: "倉別",
      dataIndex: "warehouseStorageName",
    },
    {
      title: "商品效期",
      dataIndex: "effectiveDate",
    },
    {
      title: "批號",
      dataIndex: "batch",
    },
    {
      title: "庫存量",
      dataIndex: "stock",
    },
    {
      title: "商品數量(件)",
      dataIndex: "qty",
    },
    {
      title: "實際出貨數量(件)",
      dataIndex: "actQty",
    },
    {
      title: "",
      dataIndex: "id",
      fixed: "right",
      render: (id: number, record: any) => {
        return (
          <>
            <Button
              type="link"
              onClick={() => {
                setEditProduct(record);
                setShowModal(true);
              }}
              disabled={!canEdit}
            >
              編輯
            </Button>
            <Button
              type="link"
              onClick={() => {
                Modal.confirm({
                  title: "確定要刪除此商品嗎?",
                  icon: <ExclamationCircleOutlined />,
                  okText: "是",
                  cancelText: "否",
                  onOk() {
                    dispatch(deleteSpecialShipmentProduct({ shipmentId, productId: id }));
                  },
                });
              }}
              disabled={!canEdit}
            >
              刪除
            </Button>
          </>
        );
      },
    },
  ];
  useEffect(() => {
    dispatch(fetchSpecialShipmentProductList({ shipmentId, productParams }));
  }, [dispatch, shipmentId, productParams]);

  return (
    <StyledSection>
      <Row justify="space-between" style={{ marginBottom: 21 }} align="middle">
        <Col span={12}>
          <Space>
            <Button
              type="primary"
              icon={<PlusCircleTwoTone twoToneColor="#1890FF" />}
              onClick={() => {
                setShowModal(true);
              }}
              disabled={!canEdit}
            >
              新增商品
            </Button>
          </Space>
        </Col>
      </Row>
      <TableWrapper>
        <IntegratedTable
          isPagination
          paginationConfig={{
            totalCount: count,
            updateFilterParams: (filter) => dispatch(updateProductParams(filter)),
            filterParams: productParams,
          }}
          dataSource={productResults}
          loading={isFetching}
          columns={RenTrapColumns}
          scroll={{ x: "max-content" }}
          rowKey="id"
        />
      </TableWrapper>
      {showModal && (
        <RenTrapProductFormModal
          form={modalForm}
          data={editProduct}
          warehouseCode={specialShipmentDetail?.warehouseCode}
          virtualStockRetailerId={virtualStockRetailerId}
          isConsumables={isConsumables}
          purpose={itemDetails.purpose}
          condition={itemDetails.condition}
          onClose={() => {
            setShowModal(false);
            setEditProduct(null);
          }}
          onSubmit={handleOnSubmitProduct}
        />
      )}
    </StyledSection>
  );
}

type FormProps = {
  form: FormInstance<any>;
  data: SSProductItem | null;
  warehouseCode?: string;
  virtualStockRetailerId?: number;
  onClose: () => void;
  isConsumables: boolean;
  purpose: SpecialShipmentPurpose;
  condition: GoodsCondition;
  onSubmit: (type: "create" | "update", values: any, productId?: number) => void;
};

const RenTrapProductFormModal = (props: FormProps) => {
  const { form, data, warehouseCode, virtualStockRetailerId, onClose, onSubmit, isConsumables, purpose, condition } = props;
  const isVirtualStock = virtualStockRetailerId !== undefined;
  const isWithdrawBad = purpose === SpecialShipmentPurpose.WITHDRAW && condition === GoodsCondition.BAD;
  const [skuError, setSkuError] = useState<{
    validateStatus: ValidateStatus;
    errorMsg: string | null;
  }>({ validateStatus: "", errorMsg: null });

  const [stockList, setStockList] = useState<StockList>({});
  const [effectiveDateObj, setEffectiveDateObj] = useState<StockList>({});
  const [batchOptions, setBatchOptions] = useState<{ label: string; value: string | null }[]>([]);
  const [isExpiring, setIsExpiring] = useState(false);
  const [editInit, setEditInit] = useState(false);
  const canSalesQtyFormValue = Form.useWatch("canSalesQty", form);
  const storageTypeFormValue = Form.useWatch("storageType", form);

  const expiryTypeOptions = useMemo(
    () => getAvailableExpiryTypeOptions(stockList[storageTypeFormValue] ?? []),
    [stockList, storageTypeFormValue],
  );

  const fetchProductDetailBySku = useDebounce(async (sku: string) => {
    if (!sku || !warehouseCode) return;
    if (isConsumables && !PREFIX_CONSUMABLES.some((p) => sku.startsWith(p))) {
      setSkuError({ validateStatus: "error", errorMsg: CONSUMABLES_SKU_ERROR_MSG });
      form.setFieldsValue({
        barcode: undefined,
        productName: undefined,
        weightedCost: undefined,
        canSalesQty: undefined,
      });
      return;
    }
    setSkuError({ validateStatus: "validating", errorMsg: "" });
    const result = await lookupProductBySku(sku, warehouseCode, virtualStockRetailerId);
    if (!result.ok) {
      form.setFieldsValue({ barcode: undefined });
      handleSkuLookupError(result.error, setSkuError, form);
      return;
    }
    form.setFieldsValue({
      barcode: result.info[0]?.barcode,
      productName: result.info[0]?.productName,
      weightedCost: result.info[0]?.weightedCost,
    });
    setSkuError({ validateStatus: "success", errorMsg: null });
    const newStockList = groupStockByKey(result.stock[0]?.stockInfo ?? [], "storageType");
    setStockList(newStockList);

    const defaultStorageKey = isWithdrawBad ? RentrapStorageKey.BAD : RentrapStorageKey.GOOD;
    if (!data && newStockList[defaultStorageKey]) {
      const stockData = newStockList[defaultStorageKey].filter(
        (item) => item.effectiveDate !== BACKEND_VIRTUAL_EFFECTIVE_DATE && (item.isExpiring ?? false) === isExpiring,
      );
      form.setFieldsValue({ storageType: defaultStorageKey, effectiveDate: undefined, batch: undefined });
      setEffectiveDateObj(groupStockByKey(stockData, "effectiveDate"));
    }
  }, 1000);

  const handleStockChange = (value: string) => {
    const stockData = (stockList[value] ?? []).filter(
      (item) => item.effectiveDate !== BACKEND_VIRTUAL_EFFECTIVE_DATE && (item.isExpiring ?? false) === isExpiring,
    );
    const rawObj = groupStockByKey(stockData, "effectiveDate");
    form.setFieldsValue({ effectiveDate: undefined, batch: undefined });
    setEffectiveDateObj(rawObj);
  };

  const handleEffectiveDateChange = (value: string) => {
    const batchList = effectiveDateObj[value];
    const seen = new Set<string | null>();
    const dedupedBatchList = (batchList ?? []).filter((item) => {
      if (seen.has(item.batch)) return false;
      seen.add(item.batch);
      return true;
    });
    setBatchOptions(dedupedBatchList.map((batch) => ({ label: batch.batch || "無批號", value: batch.batch || null })));
    form.setFieldsValue({ batch: undefined });
  };

  const handleOnBatchChange = (value: string | null) => {
    const effectiveDate = form.getFieldValue("effectiveDate");
    const effectiveDateKey = effectiveDate;
    const batchData = effectiveDateObj[effectiveDateKey]?.find((item) =>
      value === null ? !item.batch : item.batch === value,
    );
    form.setFieldsValue({ canSalesQty: batchData?.canSalesQty });
  };

  const handleExpiryTypeChange = (value: boolean) => {
    setIsExpiring(value);
    const storageTypeValue = form.getFieldValue("storageType");
    form.setFieldsValue({ effectiveDate: undefined, batch: undefined, canSalesQty: undefined });
    setBatchOptions([]);
    if (!storageTypeValue) {
      setEffectiveDateObj({});
      return;
    }
    const stockData = (stockList[storageTypeValue] ?? []).filter(
      (item) => item.effectiveDate !== BACKEND_VIRTUAL_EFFECTIVE_DATE && (item.isExpiring ?? false) === value,
    );
    setEffectiveDateObj(groupStockByKey(stockData, "effectiveDate"));
  };

  useEffect(() => {
    if (expiryTypeOptions.length === 0) return;
    const isCurrentValueValid = expiryTypeOptions.some((option) => option.value === isExpiring);
    if (!isCurrentValueValid) {
      form.setFieldsValue({ expiryType: expiryTypeOptions[0].value });
      handleExpiryTypeChange(expiryTypeOptions[0].value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expiryTypeOptions]);

  const handleOnClose = () => {
    form.resetFields();
    onClose();
  };

  const handleOnRenTrapSubmit = (values: any) => {
    const { sku, effectiveDate, qty, batch, storageType } = values;
    const payload = {
      sku,
      storageType,
      effectiveDate: effectiveDate === "null" ? "" : effectiveDate,
      qty,
      batch,
    };
    const type = data ? "update" : "create";
    onSubmit(type, payload, data?.id);
    handleOnClose();
  };

  // 編輯init的時候，用sku去撈商品資訊
  useEffect(() => {
    if (!data) return;
    if (editInit) return;
    fetchProductDetailBySku(data.sku);
    form.setFieldsValue({
      sku: data.sku,
      qty: data.qty,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, editInit, form]);

  // 編輯init的時候，要依序把stockList, effectiveDateObj, batchOptions都設定好
  useEffect(() => {
    if (editInit) return;
    if (!data || Object.keys(stockList).length === 0) return;
    const stockForType = stockList[data.warehouseStorageType] ?? [];
    const matchedStock = stockForType.find(
      (item) => item.effectiveDate === (data.effectiveDate || null) && item.batch === (data.batch || null),
    );
    const matchedIsExpiring = matchedStock?.isExpiring ?? false;
    setIsExpiring(matchedIsExpiring);
    const stockData = stockForType.filter(
      (item) =>
        item.effectiveDate !== BACKEND_VIRTUAL_EFFECTIVE_DATE && (item.isExpiring ?? false) === matchedIsExpiring,
    );
    setEffectiveDateObj(groupStockByKey(stockData, "effectiveDate"));
    form.setFieldsValue({
      storageType: data.warehouseStorageType,
      expiryType: matchedIsExpiring,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, editInit, form, stockList]);
  useEffect(() => {
    if (editInit) return;
    if (!data || Object.keys(effectiveDateObj).length === 0) return;
    const key = data.effectiveDate || "null";
    if (!effectiveDateObj[key]) {
      message.warning("原效期已無可用庫存，請重新選擇");
      form.setFieldsValue({ effectiveDate: undefined });
      setEditInit(true);
      return;
    }
    const selectValue = data.effectiveDate || "null";
    handleEffectiveDateChange(selectValue);
    form.setFieldsValue({
      effectiveDate: selectValue,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, editInit, form, effectiveDateObj]);
  useEffect(() => {
    if (editInit) return;
    if (!data || Object.keys(batchOptions).length === 0) return;
    const batchValue = data.batch || null;
    handleOnBatchChange(batchValue);
    form.setFieldsValue({ batch: batchValue });
    setEditInit(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, editInit, form, batchOptions]);

  return (
    <PopupBackground close={() => {}}>
      <PopupContent>
        <Form form={form} colon={false} labelCol={{ span: 8 }} labelAlign="left" onFinish={handleOnRenTrapSubmit}>
          <Spin spinning={false}>
            <Row>
              <Col span={16}>
                <Form.Item
                  label="SKU(品號)"
                  name="sku"
                  labelAlign="left"
                  hasFeedback
                  rules={buildSkuRules(isConsumables, skuError)}
                  validateStatus={skuError.validateStatus}
                  help={skuError.errorMsg || ""}
                >
                  <Input onChange={(e) => fetchProductDetailBySku(e.target.value)} />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <Form.Item label="barcode" name="barcode" labelAlign="left">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col>
                <Notice>*輸入SKU後自動帶入</Notice>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <Form.Item label="商品名稱" name="productName" labelAlign="left">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col>
                <Notice>*輸入SKU後自動帶入</Notice>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <Form.Item label="成本" name="weightedCost" labelAlign="left">
                  <Input disabled />
                </Form.Item>
              </Col>
              <Col>
                <Notice>*輸入SKU後自動帶入</Notice>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <Form.Item
                  label="倉別"
                  name="storageType"
                  labelAlign="left"
                  hidden={isVirtualStock}
                  rules={[{ required: true, message: "" }]}
                >
                  <Select
                    options={Object.keys(stockList)
                      .filter((storage) => (isWithdrawBad ? storage === RentrapStorageKey.BAD : storage !== RentrapStorageKey.BAD))
                      .map((storage) => {
                        const storageDate = storageTypeOptions.find((item) => item.key === storage);
                        return { label: storageDate?.name, value: storageDate?.value };
                      })}
                    onChange={handleStockChange}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <Form.Item
                  label="效期種類"
                  name="expiryType"
                  labelAlign="left"
                  hidden={isVirtualStock}
                  initialValue={false}
                  rules={[{ required: true, message: "" }]}
                >
                  <Select options={expiryTypeOptions} onChange={handleExpiryTypeChange} />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <Form.Item
                  label="商品效期"
                  name="effectiveDate"
                  labelAlign="left"
                  rules={[{ required: true, message: "" }]}
                >
                  <Select
                    placeholder="請先選擇倉別"
                    disabled={!form.getFieldValue("storageType")}
                    options={Object.keys(effectiveDateObj).map((date) => {
                      if (date === "null") return { label: "無效期", value: "null" };
                      return { label: date, value: date };
                    })}
                    onChange={handleEffectiveDateChange}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <Form.Item
                  label="批號"
                  name="batch"
                  labelAlign="left"
                  rules={[
                    {
                      validator: (_, value) =>
                        value !== undefined ? Promise.resolve() : Promise.reject(new Error("")),
                    },
                  ]}
                >
                  <Select
                    placeholder="請先選擇商品效期"
                    disabled={!form.getFieldValue("effectiveDate")}
                    options={batchOptions}
                    onChange={handleOnBatchChange}
                  />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <Form.Item label="庫存量" name="canSalesQty" labelAlign="left">
                  <Input disabled />
                </Form.Item>
              </Col>
            </Row>
            <Row>
              <Col span={16}>
                <Form.Item
                  label="商品數量(pcs)"
                  name="qty"
                  labelAlign="left"
                  rules={[
                    { required: true, message: "" },
                    {
                      validator: (rule, value) => {
                        if (!value) return Promise.reject(new Error(""));
                        if (value && value <= canSalesQtyFormValue) {
                          return Promise.resolve();
                        }
                        return Promise.reject(new Error("商品數量不可大於庫存量"));
                      },
                    },
                  ]}
                >
                  <InputNumber min={1} />
                </Form.Item>
              </Col>
            </Row>
            <Row justify="end">
              <Space>
                <Button type="default" onClick={() => handleOnClose()}>
                  取消
                </Button>
                <Button type="primary" htmlType="submit">
                  確認
                </Button>
              </Space>
            </Row>
          </Spin>
        </Form>
      </PopupContent>
    </PopupBackground>
  );
};
